import * as React from 'react'
import './custom-rating-scales.scss'
import {
  EditorComponent,
  OnChangeHTML,
  Remirror,
  useActive,
  useChainedCommands,
  useRemirror,
  useRemirrorContext,
} from '@remirror/react'
import {
  BoldExtension,
  BulletListExtension,
  HardBreakExtension,
  ItalicExtension,
  LinkExtension,
  OrderedListExtension,
  StrikeExtension,
  UnderlineExtension,
} from 'remirror/extensions'
import {
  Check,
  LinkSimple,
  ListBullets,
  ListNumbers,
  TextBolder,
  TextItalic,
  TextStrikethrough,
  TextUnderline,
  X,
} from '@phosphor-icons/react'
import 'remirror/styles/core.css'
import 'remirror/styles/extension-list.css'

type ToolbarButtonProps = {
  onClick: () => void
  active?: boolean
  label: string
  children: React.ReactNode
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  active,
  label,
  children,
}) => (
  <button
    type="button"
    title={label}
    onMouseDown={(e) => {
      e.preventDefault()
      onClick()
    }}
    className={`inline-flex h-8 w-8 items-center justify-center rounded transition-colors ${
      active ? 'bg-white/80 text-[#1D264A]' : 'text-[#5E6580] hover:bg-white/60'
    }`}
  >
    {children}
  </button>
)

const Separator = () => <div className="mx-2 h-8 w-px bg-[#C7CEDD]" />

const FixedToolbar: React.FC = () => {
  const chain = useChainedCommands()
  const active = useActive()
  const [linkInputOpen, setLinkInputOpen] = React.useState(false)
  const [linkUrl, setLinkUrl] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (linkInputOpen) inputRef.current?.focus()
  }, [linkInputOpen])

  const applyLink = () => {
    const href = linkUrl.trim()
    if (href) chain.updateLink({ href, target: '_blank' }).focus().run()
    setLinkInputOpen(false)
    setLinkUrl('')
  }

  const cancelLink = () => {
    setLinkInputOpen(false)
    setLinkUrl('')
    chain.focus().run()
  }

  return (
    <div>
      <div className="flex items-center gap-1 px-5 pt-4 pb-2">
        <ToolbarButton
          label="Bold"
          active={active.bold()}
          onClick={() => chain.toggleBold().focus().run()}
        >
          <TextBolder size={18} weight="bold" />
        </ToolbarButton>

        <ToolbarButton
          label="Italic"
          active={active.italic()}
          onClick={() => chain.toggleItalic().focus().run()}
        >
          <TextItalic size={18} weight="bold" />
        </ToolbarButton>

        <ToolbarButton
          label="Underline"
          active={active.underline()}
          onClick={() => chain.toggleUnderline().focus().run()}
        >
          <TextUnderline size={18} weight="bold" />
        </ToolbarButton>

        <ToolbarButton
          label="Strikethrough"
          active={active.strike()}
          onClick={() => chain.toggleStrike().focus().run()}
        >
          <TextStrikethrough size={18} weight="bold" />
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          label="Ordered list"
          active={active.orderedList()}
          onClick={() => chain.toggleOrderedList().focus().run()}
        >
          <ListNumbers size={18} weight="bold" />
        </ToolbarButton>

        <ToolbarButton
          label="Bullet list"
          active={active.bulletList()}
          onClick={() => chain.toggleBulletList().focus().run()}
        >
          <ListBullets size={18} weight="bold" />
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          label="Link"
          active={active.link() || linkInputOpen}
          onClick={() => setLinkInputOpen((o) => !o)}
        >
          <LinkSimple size={18} weight="bold" />
        </ToolbarButton>
      </div>

      {linkInputOpen && (
        <div className="flex items-center gap-2 border-t border-[#D9DDE7] px-5 py-2">
          <input
            ref={inputRef}
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                cancelLink()
              }
            }}
            placeholder="https://"
            className="min-w-0 flex-1 rounded border border-[#C7CEDD] bg-white px-3 py-1 text-sm text-[#1D264A] outline-none focus:border-[#6B7ADE]"
          />
          <button
            type="button"
            title="Apply link"
            onMouseDown={(e) => {
              e.preventDefault()
              applyLink()
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-[#39C674] hover:bg-white/60"
          >
            <Check size={16} weight="bold" />
          </button>
          <button
            type="button"
            title="Cancel"
            onMouseDown={(e) => {
              e.preventDefault()
              cancelLink()
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-[#5E6580] hover:bg-white/60"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}
    </div>
  )
}

const EXTENSIONS = () => [
  new BoldExtension({}),
  new ItalicExtension(),
  new UnderlineExtension(),
  new StrikeExtension(),
  new OrderedListExtension(),
  new BulletListExtension({}),
  new LinkExtension({ autoLink: true, defaultTarget: '_blank' }),
  new HardBreakExtension(),
]

type ExternalValueSyncProps = {
  value: string
  onChange: (html: string) => void
}

// Remirror is uncontrolled after mount, so we track the last value emitted by
// the editor and call setContent only when the parent changes it from outside
// (e.g. Cancel or switching between scales in edit mode).
const ExternalValueSync: React.FC<ExternalValueSyncProps> = ({
  value,
  onChange,
}) => {
  const { setContent } = useRemirrorContext()
  const lastInternalRef = React.useRef<string>(value)

  React.useEffect(() => {
    if (value !== lastInternalRef.current) {
      setContent(value || '', { triggerChange: false })
      lastInternalRef.current = value
    }
  }, [value, setContent])

  return (
    <OnChangeHTML
      onChange={(html) => {
        const textContent = html.replace(/<[^>]*>/g, '').trim()
        const normalized = textContent ? html : ''
        if (normalized === lastInternalRef.current) return
        lastInternalRef.current = normalized
        onChange(normalized)
      }}
    />
  )
}

type DescriptionTextareaProps = {
  value: string
  onChange: (value: string) => void
  error?: string
}

export const DescriptionTextarea: React.FC<DescriptionTextareaProps> = ({
  value,
  onChange,
  error,
}) => {
  const { manager, state } = useRemirror({
    extensions: EXTENSIONS,
    content: value || undefined,
    stringHandler: 'html',
  })

  return (
    <div>
      <div
        className={`rounded-lg bg-[#EFF2F9] ${
          error ? 'border border-red-500' : 'border border-transparent'
        }`}
      >
        <Remirror manager={manager} initialContent={state}>
          <FixedToolbar />
          <ExternalValueSync value={value} onChange={onChange} />
          <div className="description-editor-content min-h-[120px] px-5 pb-5 text-[16px] font-normal leading-8 text-[#1D264A]">
            <EditorComponent />
          </div>
        </Remirror>
      </div>
      {error && <p className="mt-2 mb-0 text-sm text-red-600">{error}</p>}
    </div>
  )
}
